import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from "react";

interface MailboxItemContextValue {
  currentItem: Office.MessageRead | Office.MessageCompose | Office.AppointmentRead | Office.AppointmentCompose | null;
  itemId: string | null;
  changeCount: number;
  /** Re-read the current mailbox item and trigger a reload — without a document reload. */
  resync: () => void;
}

interface MailboxItemProviderProps {
  children: ReactNode;
}

const MailboxItemContext = createContext<MailboxItemContextValue | undefined>(undefined);

export function MailboxItemProvider({ children }: MailboxItemProviderProps) {
  const [currentItem, setCurrentItem] = useState<
    Office.MessageRead | Office.MessageCompose | Office.AppointmentRead | Office.AppointmentCompose | null
  >(null);
  const [itemId, setItemId] = useState<string | null>(null);
  const [changeCount, setChangeCount] = useState(0);
  // Holds the latest sync fn so the stable `resync` callback can re-run it.
  const syncRef = useRef<() => void>(() => {});

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    const POLL_MS = 300;
    const MAX_POLLS = 6; // ~1.8s grace window

    // Commit whatever item is currently available to context. Office.context.mailbox
    // can be undefined on hosts that fail to fully initialize the bridge — read
    // defensively. Returns true if an item was present.
    const commit = () => {
      const item = Office.context?.mailbox?.item ?? null;
      setCurrentItem(item);
      setItemId(item && "itemId" in item ? item.itemId || null : null);
      setChangeCount((prev) => prev + 1);
      return item != null;
    };

    // ItemChanged *is* the "the item changed" signal — re-read on every event and
    // never gate on itemId (shared/compose items surface a null itemId).
    //
    // Classic Outlook 2606 regressed the pinned-pane lifecycle: ItemChanged can
    // fire with Office.context.mailbox.item === null and then NOT deliver the
    // follow-up event carrying the real item (office-js #2295/#5965). Committing
    // that null immediately strands the user on the "no item" screen. So on a null
    // item we don't trust a future event — poll briefly for the item to
    // materialize before committing. A genuine deselect stays null the whole
    // window and correctly ends on "no item".
    const sync = () => {
      if (cancelled) return;
      clearInterval(pollTimer);
      if (commit()) return; // item already present
      let attempts = 0;
      pollTimer = setInterval(() => {
        if (cancelled) {
          clearInterval(pollTimer);
          return;
        }
        attempts += 1;
        const hasItem = (Office.context?.mailbox?.item ?? null) != null;
        if (hasItem || attempts >= MAX_POLLS) {
          clearInterval(pollTimer);
          commit();
        }
      }, POLL_MS);
    };
    syncRef.current = sync;

    sync(); // initial load

    const mailbox = Office.context?.mailbox;
    if (mailbox?.addHandlerAsync) {
      mailbox.addHandlerAsync(Office.EventType.ItemChanged, sync, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.warn("Failed to register ItemChanged handler:", result.error);
        }
      });
    }
    // ponytail: no removeHandlerAsync — the provider lives for the app's lifetime.
    return () => {
      cancelled = true;
      clearInterval(pollTimer);
    };
  }, []);

  // Soft retry: re-read the current item and reload WITHOUT window.location.reload().
  // On 2606 a hard reload re-triggers the broken pinned-pane init and hangs
  // ("Outlook failed to start") or crashes (office-js #6686); re-syncing keeps the
  // already-working Office bridge alive.
  const resync = useCallback(() => syncRef.current(), []);

  return (
    <MailboxItemContext.Provider value={{ currentItem, itemId, changeCount, resync }}>
      {children}
    </MailboxItemContext.Provider>
  );
}

export function useMailboxItem() {
  const context = useContext(MailboxItemContext);
  if (context === undefined) {
    throw new Error("useMailboxItem must be used within a MailboxItemProvider");
  }
  return context;
}
